import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, getIdToken } from 'firebase/auth';
import SpendingBreakdown from './SpendingBreakdown';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import B2BPortal from './pages/B2BPortal';
import FinopsDashboard from './pages/FinopsDashboard';
import { useTranslation } from 'react-i18next';
import './App.css'; // Aseguramos que se importen los estilos
import ModuleGuard from './components/ModuleGuard';

// URLs de las APIs. En producción, estas se obtienen de las variables de entorno.
const PAYMENTS_API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api/finance/payments/` : 'https://backend-core-644060845776.us-central1.run.app/api/finance/payments/';
const AUTH_SERVICE_URL = (process.env.REACT_APP_BACKEND_URL || 'https://backend-core-644060845776.us-central1.run.app') + '/api';

// Tu configuración de Firebase
const firebaseConfig = {
  apiKey: "AIzaSyCs00Lks78yR6ETS-Cz5WpWiDW9dDQpHTU",
  authDomain: "app.on-track-app.com",
  projectId: "my-payments-agenda-2bdf0",
  storageBucket: "my-payments-agenda-2bdf0.firebasestorage.app",
  messagingSenderId: "644060845776",
  appId: "1:644060845776:web:6c7f156f1bf71da840f0d5"
};

// Inicializa la app de Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Helper para sanitizar fechas DD-MM-YYYY o DD/MM/YYYY a YYYY-MM-DD (ISO).
// Usa Regex estricta para evitar que el constructor Date() invierta día y mes.
const parseIsoDate = (dateStr) => {
  if (!dateStr) return null;
  // Detecta exactamente DD-MM-YYYY o DD/MM/YYYY
  const match = String(dateStr).match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (match) {
    const day   = match[1];
    const month = match[2];
    const year  = match[3];
    return `${year}-${month}-${day}`; // Fuerza YYYY-MM-DD para el constructor Date
  }
  return dateStr; // Fallback: ISO nativo u otro formato — no se altera
};

// Helper para formatear la moneda
const formatCurrency = (amount, currency, locale = 'es') => {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
};

// Componente para renderizar la lista de pagos
const PaymentList = ({ payments, accentColor = 'var(--primary-color)', onMarkAsPaid }) => {
  const { t, i18n } = useTranslation();

  // Mapeo de códigos de categoría a ícono + etiqueta legible
  const CATEGORY_MAP = {
    UTILITIES:     { icon: '💡', label: 'Servicios' },
    TELECOM:       { icon: '📡', label: 'Telecomunicaciones' },
    INSURANCE:     { icon: '🛡️', label: 'Seguros' },
    CREDIT_CARD:   { icon: '💳', label: 'Tarjeta de Crédito' },
    SUBSCRIPTION:  { icon: '🔄', label: 'Suscripción' },
    BANKING:       { icon: '🏦', label: 'Banca' },
    HEALTHCARE:    { icon: '🏥', label: 'Salud' },
    EDUCATION:     { icon: '📚', label: 'Educación' },
    OTHERS:        { icon: '📦', label: 'Otros' },
  };

  const getCategoryBadge = (rawCategory) => {
    const key = (rawCategory || '').toUpperCase().trim();
    const meta = CATEGORY_MAP[key];
    if (meta) {
      return { icon: meta.icon, label: meta.label, hasCategory: true };
    }
    return { icon: '🏷️', label: 'Sin Categorizar', hasCategory: false };
  };

  if (payments.length === 0) {
    return <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '40px' }}>{t('app.no_payments_category')}</p>;
  }

  return (
    <ul style={{ listStyleType: 'none', padding: 0 }}>
      {payments.map(payment => {
        const itemStyle = {
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderLeft: `5px solid ${payment.due_date ? accentColor : '#94A3B8'}`,
          padding: '20px',
          margin: '15px 0',
          borderRadius: '1rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        };

        const { icon: catIcon, label: catLabel, hasCategory } = getCategoryBadge(payment.spending_category);
        const badgeStyle = {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '0.75em',
          fontWeight: '600',
          padding: '3px 10px',
          borderRadius: '999px',
          marginTop: '6px',
          background: hasCategory ? 'rgba(99, 102, 241, 0.15)' : 'rgba(148, 163, 184, 0.12)',
          color: hasCategory ? '#A5B4FC' : '#94A3B8',
          border: `1px solid ${hasCategory ? 'rgba(99, 102, 241, 0.3)' : 'rgba(148, 163, 184, 0.2)'}`,
          letterSpacing: '0.02em',
        };

        return (
          <li key={payment.id} style={itemStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ marginRight: '12px', fontSize: '1.5em', background: 'rgba(255,255,255,0.1)', padding: '8px', borderRadius: '12px' }}>
                {parseInt(payment.id, 10) % 2 === 0 ? '📎' : '📧'}
              </span>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '1.1em' }}>{payment.biller_name}</p>
                <span style={badgeStyle}>
                  {catIcon} {catLabel}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginTop: '8px' }}>
              <div>
                <p style={{ margin: '0', fontSize: '1.5em', fontWeight: '800', color: 'var(--text-primary)' }}>
                  {formatCurrency(payment.amount, payment.currency, i18n.language)}
                </p>
                <p style={{ color: 'var(--text-secondary)', fontWeight: 'normal', margin: '5px 0 0 0', fontSize: '0.85em' }}>
                  <strong>{t('app.due')}:</strong> {(() => {
                    try {
                      const isoDate = parseIsoDate(payment.due_date);
                      return isoDate ? new Date(`${isoDate}T00:00:00`).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }) : t('app.na');
                    } catch (e) {
                      return payment.due_date || t('app.na');
                    }
                  })()}
                </p>
              </div>
              <button
                onClick={() => onMarkAsPaid(payment.id)}
                style={{ fontFamily: 'inherit', fontSize: '0.85em', padding: '8px 16px', cursor: 'pointer', border: '1px solid #10B981', background: 'rgba(16, 185, 129, 0.1)', color: '#10B981', borderRadius: '8px', fontWeight: '600', transition: 'all 0.2s' }}>
                {t('app.mark_paid')}
              </button>
            </div>
          </li>
        );
      })}
    </ul >
  );
};


// Iconos SVG
const GmailIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OutlookIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 3H4C2.89543 3 2 3.89543 2 5V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V5C22 3.89543 21.1046 3 20 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 7L12 14L2 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="15" y="12" width="5" height="5" stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);

function MainApp() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [isOutlookConnected, setIsOutlookConnected] = useState(false);
  const [payments, setPayments] = useState([]);
  const [syncStatus, setSyncStatus] = useState('IDLE'); // 'IDLE' | 'SYNCING' | 'COMPLETED' | 'FAILED'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('urgent');

  const [currencyFilter, setCurrencyFilter] = useState('ALL'); // 'ALL' | 'CRC' | 'USD'
  const [analyticsData, setAnalyticsData] = useState(null);
  const [insightText, setInsightText] = useState(null);

  const [urgentPayments, setUrgentPayments] = useState([]);
  const [nextWeekPayments, setNextWeekPayments] = useState([]);
  const [futurePayments, setFuturePayments] = useState([]);
  const [historyPayments, setHistoryPayments] = useState([]);

  const provider = new GoogleAuthProvider();

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await getIdToken(user, true);
      const redirectUrl = searchParams.get('redirect');
      const currentPath = window.location.pathname;
      let desiredRole = 'B2C';
      if (redirectUrl === '/b2b' || currentPath === '/b2b') {
        desiredRole = 'B2B';
      } else if (redirectUrl === '/finops' || currentPath === '/finops') {
        desiredRole = 'FinOps';
      }

      const response = await axios.post(`${AUTH_SERVICE_URL}/auth/register_or_login`, { 
        token: idToken,
        desired_role: desiredRole
      });

      if (response.data && response.data.user_id) {
        setUserId(response.data.user_id);
      }

      if (desiredRole === 'B2B' || desiredRole === 'FinOps') {
        await user.getIdToken(true); // Force token refresh to sync B2B or FinOps claims immediately
      }
    } catch (error) {
      console.error("Error de inicio de sesión con Google:", error);
      setError("Error de autenticación. Revisa la consola.");
    }
  };

  /* FLUJO DEPRECADO: Microsoft Auth temporalmente deshabilitado
  const handleMicrosoftSignIn = async () => {
    try {
      const msProvider = new OAuthProvider('microsoft.com');
      const result = await signInWithPopup(auth, msProvider);
      const user = result.user;
      const idToken = await getIdToken(user, true);

      // Reutilizamos el endpoint existente, ya que valida el UID de Firebase
      const response = await axios.post(`${AUTH_SERVICE_URL}/auth/register_or_login`, { token: idToken });

      if (response.data && response.data.user_id) {
        setUserId(response.data.user_id);
      }
    } catch (error) {
      console.error("Error de inicio de sesión con Microsoft:", error);
      setError("Error de autenticación con Microsoft.");
    }
  };
  */

  const handleGmailConnect = () => {
    if (!userId) return;
    window.location.href = `${AUTH_SERVICE_URL}/auth/google/authorize?user_id=${userId}`;
  };

  const handleOutlookConnect = () => {
    if (!userId) return;
    // Redirigir directamente al endpoint que inicia el flujo
    window.location.href = `${AUTH_SERVICE_URL}/auth/microsoft/login?user_id=${userId}`;
  };

  const checkConnections = async (token) => {
    try {
      // Endpoint actualizado para verificar todas las conexiones
      const response = await axios.get(`${AUTH_SERVICE_URL}/auth/connections/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setIsGmailConnected(!!response.data.is_gmail_connected);
        setIsOutlookConnected(!!response.data.is_outlook_connected);
        if (response.data.last_financial_insight) {
          setInsightText(response.data.last_financial_insight);
        }
        if (response.data.sync_status) {
          setSyncStatus(prevStatus => {
            // Prevenir sobrescribir SYNCING forzado con el IDLE inicial del backend 
            // antes de que el worker tenga tiempo de iniciar.
            if (prevStatus === 'SYNCING' && response.data.sync_status === 'IDLE') {
              return 'SYNCING';
            }
            return response.data.sync_status;
          });
        }
      }
    } catch (error) {
      console.error("Error al verificar conexiones:", error);
      // Fallback a endpoint antiguo si el nuevo falla (opcional, por si acaso)
      // setIsGmailConnected(false);
    }
  };

  const fetchPayments = async (token, currency = 'ALL') => {
    try {
      setLoading(true);
      let url = PAYMENTS_API_URL;
      if (currency !== 'ALL') {
        url += `?currency=${currency}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error al obtener los pagos:", error);
      setError("No se pudieron cargar los pagos. Asegúrate de que el backend esté corriendo.");
      setLoading(false);
    }
  };

  const fetchAnalytics = async (token) => {
    try {
      setLoading(true);
      const response = await axios.get(`${PAYMENTS_API_URL}analytics`, { // Note: PAYMENTS_API_URL ends in /
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalyticsData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error al obtener analytics:", error);
      // No setear error global para no bloquear la UI entera si falla solo analytics
      setLoading(false);
    }
  };

  // Efecto para recargar cuando cambia el filtro de moneda (si no estamos en analytics)
  useEffect(() => {
    if (user && activeTab !== 'analytics') {
      getIdToken(user).then(token => fetchPayments(token, currencyFilter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyFilter, user]);

  // Efecto para cargar analytics cuando se entra a esa tab
  useEffect(() => {
    if (user && activeTab === 'analytics') {
      getIdToken(user).then(token => fetchAnalytics(token));
    }
  }, [activeTab, user]);

  const handleRefresh = async () => {
    if (!user) return;
    setError(null);
    const token = await getIdToken(user);
    if (activeTab === 'analytics') {
      await fetchAnalytics(token);
    } else {
      await fetchPayments(token, currencyFilter);
    }
  };

  const handleMarkAsPaid = async (paymentId) => {
    const token = await getIdToken(user);
    try {
      await axios.patch(`${PAYMENTS_API_URL}${paymentId}`,
        { is_paid: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayments(prevPayments => prevPayments.filter(p => p.id !== paymentId));

    } catch (error) {
      console.error("Error al marcar el pago como realizado:", error);
      setError("No se pudo actualizar el pago. Inténtalo de nuevo.");
      setTimeout(() => setError(null), 5000);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        let token = await getIdToken(currentUser);
        try {
          const redirectUrl = searchParams.get('redirect');
          const currentPath = window.location.pathname;
          let desiredRole = 'B2C';
          if (redirectUrl === '/b2b' || currentPath === '/b2b') {
            desiredRole = 'B2B';
          } else if (redirectUrl === '/finops' || currentPath === '/finops') {
            desiredRole = 'FinOps';
          }

          const response = await axios.post(`${AUTH_SERVICE_URL}/auth/register_or_login`, { 
            token: token,
            desired_role: desiredRole
          });
          if (response.data && response.data.user_id) {
            setUserId(response.data.user_id);
          }
          setUser(currentUser);

          // Force Firebase token refresh to obtain the updated Custom Claims immediately (if B2B or FinOps onboarding is requested)
          if (desiredRole === 'B2B' || desiredRole === 'FinOps') {
            token = await currentUser.getIdToken(true);
          }

          // Send locale to backend
          try {
            await axios.patch(`${AUTH_SERVICE_URL}/auth/locale`, { locale: i18n.language.split('-')[0] }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (err) {
            console.error("Failed to sync locale:", err);
          }

          try {
            const idTokenResult = await currentUser.getIdTokenResult(true); // Force loading updated claims
            const role = (idTokenResult.claims?.role || '').toUpperCase();
            if (redirectUrl === '/b2b') {
                if (role === 'B2B' || role === 'ADMIN') {
                    navigate('/b2b', { replace: true });
                } else {
                    navigate('/', { replace: true }); 
                }
                return;
            }
            if (redirectUrl === '/finops') {
                navigate('/finops', { replace: true });
                return;
            }
          } catch (err) {
            console.error("Routing resolution failed:", err);
          }

          // Verificar parámetros de URL para éxito de OAuth (Outlook o Gmail)
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('oauth') === 'success' || urlParams.get('microsoft_oauth') === 'success') {
            setSyncStatus('SYNCING'); // Force UI to show syncing immediately for better UX
            // Limpiar URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          await checkConnections(token);
          await fetchPayments(token);
        } catch (e) {
          setError("No se pudo verificar el usuario con el backend.");
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
        setIsGmailConnected(false);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling para sync_status
  useEffect(() => {
    let intervalId;
    if (user && syncStatus === 'SYNCING') {
      intervalId = setInterval(async () => {
        try {
          const token = await getIdToken(user);
          await checkConnections(token);
          // Si cambió a COMPLETED, refrescamos pagos
          // Nota: checkConnections actualiza syncStatus. 
          // Si el backend reporta IDLE/COMPLETED, el polling se detendrá en el proximo render.
          // Pero necesitamos refrescar los pagos si acabamos de terminar.

          // Una mejor estrategia en checkConnections sería retornar el status.
          // Pero aquí podemos simplemente refrescar pagos si vemos COMPLETED/IDLE.
          // O simplemente llamamos refresh siempre que termine.

          // Vamos a hacerlo simple: App.js detectará cambio de syncStatus en otro effect y recargará?
          // No, mejor aquí:
          const response = await axios.get(`${AUTH_SERVICE_URL}/auth/connections/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const newStatus = response.data?.sync_status;

          if (newStatus === 'COMPLETED' || (newStatus === 'IDLE' && syncStatus === 'SYNCING')) {
            await fetchPayments(token);
            setSyncStatus(newStatus); // Esto detendrá el polling
          } else {
            setSyncStatus(newStatus);
          }

        } catch (e) {
          console.error("Error polling sync status", e);
        }
      }, 3000);
    }
    return () => clearInterval(intervalId);
  }, [user, syncStatus]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(today.getDate() + 7);

    const fourteenDaysFromNow = new Date();
    fourteenDaysFromNow.setDate(today.getDate() + 14);

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const urgent = [];
    const nextWeek = [];
    const future = [];
    const history = [];

    payments.forEach(p => {
      if (p.is_paid) return;

      if (p.amount === null || p.amount <= 0 || !p.due_date) {
        future.push(p);
        return;
      }
      const isoDate = parseIsoDate(p.due_date);
      const dueDate = new Date(`${isoDate}T00:00:00`);

      if (dueDate < today) {
        // Show all historical payments, even if older than 30 days
        history.push(p);
        return;
      }

      if (dueDate < sevenDaysFromNow) {
        urgent.push(p);
      } else if (dueDate >= sevenDaysFromNow && dueDate < fourteenDaysFromNow) {
        nextWeek.push(p);
      } else {
        future.push(p);
      }
    });

    const sortByDueDate = (a, b) => {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(parseIsoDate(a.due_date)) - new Date(parseIsoDate(b.due_date));
    };
    const sortHistoryByCreatedAt = (a, b) => {
      // Fallback to due_date if created_at is missing, but created_at should be present
      const dateA = new Date(parseIsoDate(a.created_at || a.due_date));
      const dateB = new Date(parseIsoDate(b.created_at || b.due_date));
      return dateB - dateA; // Descending
    };

    setUrgentPayments(urgent.sort(sortByDueDate));
    setNextWeekPayments(nextWeek.sort(sortByDueDate));
    setFuturePayments(future.sort(sortByDueDate));
    setHistoryPayments(history.sort(sortHistoryByCreatedAt));

  }, [payments]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--text-secondary)' }}>{t('app.loading')}</div>;

  // Caso A: Usuario nuevo sincronizando (Skeleton / Pantalla de Carga)
  // Si no hay pagos y está sincronizando, bloqueamos para dar feedback de "Primer uso"
  if (payments.length === 0 && syncStatus === 'SYNCING') {
    return (
      <div style={{ padding: '20px', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '20px', animation: 'spin 2s linear infinite' }}>⚙️</div>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>{t('app.analyzing_email')}</h2>
        <p style={{ color: 'var(--text-secondary)', maxWidth: '400px' }}>
          {t('app.analyzing_desc')}
        </p>
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))', paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', fontFamily: "'Inter', system-ui, sans-serif", maxWidth: '600px', margin: '0 auto', minHeight: '100vh', position: 'relative' }}>
      {!user ? (
        <div style={{ textAlign: 'center', paddingTop: '100px' }}>
          <div className="logo-text" style={{ marginBottom: '20px' }}>On<span className="logo-accent">Track</span></div>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '40px', fontSize: '1.2em' }}>{t('app.login_subtext')}</p>
          <button onClick={handleGoogleSignIn} className="btn-primary" style={{ marginBottom: '15px', width: '100%', maxWidth: '300px' }}>{t('app.login_google')}</button>
          {/* FLUJO DEPRECADO:
          <br />
          <button onClick={handleMicrosoftSignIn} className="btn-primary" style={{ background: '#2f2f2f', border: '1px solid #555', width: '100%', maxWidth: '300px' }}>{t('app.login_microsoft')}</button> */}

          {/* Footer Legal */}
          <div style={{ marginTop: '50px', fontSize: '0.8em', color: '#94a3b8' }}>
            <p>
              {t('app.terms_accept')} <Link to="/terms" style={{ color: '#4f46e5', textDecoration: 'none' }}>{t('app.terms_link')}</Link> y <Link to="/privacy" style={{ color: '#4f46e5', textDecoration: 'none' }}>{t('app.privacy_link')}</Link>.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            {/* Logo pequeño a la izquierda */}
            <div className="logo-text" style={{ fontSize: '1.5rem' }}>On<span className="logo-accent">Track</span></div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{user.displayName?.split(' ')[0]}</p>
              <button onClick={() => auth.signOut()} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', cursor: 'pointer', padding: '5px 10px', borderRadius: '6px', fontSize: '0.8rem' }}>{t('app.logout')}</button>
            </div>
          </div>

          {/* Sección de Conexión Minimalista */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '40px' }}>
            {/* Gmail Toggle */}
            <div
              onClick={!isGmailConnected ? handleGmailConnect : undefined}
              title={isGmailConnected ? t('app.gmail_connected') : t('app.gmail_connect')}
              style={{
                cursor: !isGmailConnected ? 'pointer' : 'default',
                opacity: isGmailConnected ? 1 : 0.5,
                filter: isGmailConnected ? 'none' : 'grayscale(100%)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <div style={{ color: '#EA4335' }}><GmailIcon /></div>
              {isGmailConnected && <span style={{ color: '#10B981', fontSize: '1.2em' }}>✔</span>}
            </div>

            {/* Outlook Toggle */}
            <div
              onClick={!isOutlookConnected ? handleOutlookConnect : undefined}
              title={isOutlookConnected ? t('app.outlook_connected') : t('app.outlook_connect')}
              style={{
                cursor: !isOutlookConnected ? 'pointer' : 'default',
                opacity: isOutlookConnected ? 1 : 0.5,
                filter: isOutlookConnected ? 'none' : 'grayscale(100%)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <div style={{ color: '#0078D4' }}><OutlookIcon /></div>
              {isOutlookConnected && <span style={{ color: '#10B981', fontSize: '1.2em' }}>✔</span>}
            </div>
          </div>


          {/* Selector de Moneda (Solo visible si no estamos en analytics) */}
          {activeTab !== 'analytics' && (
            <div style={{ display: 'flex', justifySelf: 'center', marginBottom: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '4px', width: 'fit-content', margin: '0 auto 20px auto' }}>
              {['ALL', 'CRC', 'USD'].map(curr => (
                <button
                  key={curr}
                  onClick={() => setCurrencyFilter(curr)}
                  style={{
                    background: currencyFilter === curr ? 'var(--primary-color)' : 'transparent',
                    color: currencyFilter === curr ? '#fff' : 'var(--text-secondary)',
                    border: 'none',
                    padding: '6px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9em',
                    transition: 'all 0.2s'
                  }}
                >
                  {curr === 'ALL' ? t('app.currency_all') : (curr === 'USD' ? '$ USD' : '₡ CRC')}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>{t('app.your_payments')}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {syncStatus === 'SYNCING' && payments.length > 0 && (
                <span style={{ fontSize: '0.8rem', color: 'var(--primary-color)', animation: 'pulse 1.5s infinite' }}>
                  {t('app.syncing')}
                </span>
              )}
              <button onClick={handleRefresh} disabled={loading || syncStatus === 'SYNCING'} style={{ background: 'none', border: 'none', color: 'var(--primary-color)', fontSize: '1.5em', cursor: 'pointer', padding: '0 5px', transition: 'transform 0.5s', transform: (loading || syncStatus === 'SYNCING') ? 'rotate(360deg)' : 'none' }}>
                {loading || syncStatus === 'SYNCING' ? '...' : '🔄'}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes pulse { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }
          `}</style>

          <div style={{ display: 'flex', background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '4px', margin: '20px 0', overflowX: 'auto' }}>
            <button
              style={{ flex: 1, padding: '8px 12px', border: 'none', background: activeTab === 'urgent' ? 'var(--card-bg)' : 'transparent', cursor: 'pointer', borderRadius: '8px', fontWeight: '600', color: activeTab === 'urgent' ? '#fff' : '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
              onClick={() => setActiveTab('urgent')}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#EF4444', marginRight: '6px' }}></span>
              {t('app.urgent')} <span style={{ marginLeft: '6px', opacity: 0.7, fontSize: '0.9em' }}>({urgentPayments.length})</span>
            </button>
            <button
              style={{ flex: 1, padding: '8px 12px', border: 'none', background: activeTab === 'nextWeek' ? 'var(--card-bg)' : 'transparent', cursor: 'pointer', borderRadius: '8px', fontWeight: '600', color: activeTab === 'nextWeek' ? '#fff' : '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
              onClick={() => setActiveTab('nextWeek')}>
              {t('app.next_week')} <span style={{ marginLeft: '6px', opacity: 0.7, fontSize: '0.9em' }}>({nextWeekPayments.length})</span>
            </button>
            <button
              style={{ flex: 1, padding: '8px 12px', border: 'none', background: activeTab === 'future' ? 'var(--card-bg)' : 'transparent', cursor: 'pointer', borderRadius: '8px', fontWeight: '600', color: activeTab === 'future' ? '#fff' : '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
              onClick={() => setActiveTab('future')}>
              {t('app.future')} <span style={{ marginLeft: '6px', opacity: 0.7, fontSize: '0.9em' }}>({futurePayments.length})</span>
            </button>
            <button
              style={{ flex: 1, padding: '8px 12px', border: 'none', background: activeTab === 'history' ? 'var(--card-bg)' : 'transparent', cursor: 'pointer', borderRadius: '8px', fontWeight: '600', color: activeTab === 'history' ? '#fff' : '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
              onClick={() => setActiveTab('history')}>
              {t('app.history')} <span style={{ marginLeft: '6px', opacity: 0.7, fontSize: '0.9em' }}>({historyPayments.length})</span>
            </button>
            <button
              style={{ flex: 1, padding: '8px 12px', border: 'none', background: activeTab === 'analytics' ? 'var(--card-bg)' : 'transparent', cursor: 'pointer', borderRadius: '8px', fontWeight: '600', color: activeTab === 'analytics' ? '#fff' : '#94A3B8', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
              onClick={() => setActiveTab('analytics')}>
              📊
            </button>
          </div>

          <div>
            {activeTab === 'urgent' && <PaymentList payments={urgentPayments} accentColor="#EF4444" onMarkAsPaid={handleMarkAsPaid} />}
            {activeTab === 'nextWeek' && <PaymentList payments={nextWeekPayments} accentColor="#F59E0B" onMarkAsPaid={handleMarkAsPaid} />}
            {activeTab === 'future' && <PaymentList payments={futurePayments} accentColor="#10B981" onMarkAsPaid={handleMarkAsPaid} />}
            {activeTab === 'history' && <PaymentList payments={historyPayments} accentColor="#64748B" onMarkAsPaid={handleMarkAsPaid} />}
            {activeTab === 'analytics' && <SpendingBreakdown payments={payments} insightText={insightText} analyticsData={analyticsData} />}
          </div>

          {payments.length === 0 && !loading && activeTab !== 'analytics' && (
            <div style={{ textAlign: 'center', padding: '60px 20px', opacity: 0.5 }}>
              <div style={{ fontSize: '3em', marginBottom: '20px' }}>😴</div>
              <p style={{ margin: 0, fontSize: '1.1em', color: 'var(--text-secondary)' }}>
                {t('app.no_pending_payments')}
              </p>
            </div>
          )}
        </div>
      )}

      {
        error && (
          <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: '#EF4444', color: 'white', padding: '10px 20px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}>
            {error}
          </div>
        )
      }
    </div >
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/b2b" element={
          <ModuleGuard requiredRole="B2B">
            <B2BPortal />
          </ModuleGuard>
        } />
        <Route path="/finops" element={
          <ModuleGuard requiredRole="FinOps">
            <FinopsDashboard />
          </ModuleGuard>
        } />
        {/* Catch-all redirect for /index.html or unknown paths */}
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;